import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import DeviceTableHeader from "./DevicesTableHeader";
import DeviceRow from "./Rows/DeviceRow";
import {
  EDIT_COLUMNS,
  VIEW_COLUMNS,
} from "../../../config/deviceColumnsConfig";
import { useBuildingDevices } from "../../../hooks/useBuildingDevices";
import {
  setSelectedBuildingDevices,
  clearSelectedBuildingDevices,
  selectSelectedBuildingDevices,
} from "../../../../../../../redux/buildings/selectedBuildingDevicesSlice";
import useDebounce from "../../../../../../../hooks/useDebounce";
import useAutoSaveBuildingDevices from "../../../hooks/useAutoSaveBuildingDevices";

const BuildingDevicesTable = ({
  buildingID,
  isEditMode,
  newDeviceId,
  setNewDeviceId,
  onAddNew,
}) => {
  const dispatch = useDispatch();
  const selectedBuildingDevice = useSelector(selectSelectedBuildingDevices);

  const {
    assignedDevices,
    deviceOptions,
    loadAssignedDevices,
    loadDeviceOptions,
    addDeviceToBuilding,
    removeDeviceFromBuilding,
    updateDeviceData,
  } = useBuildingDevices(buildingID);

  const [showAllDevices, setShowAllDevices] = useState(false);

  // Load assigned devices when the buildingID changes.
  useEffect(() => {
    if (buildingID) {
      loadAssignedDevices();
    }
  }, [buildingID, loadAssignedDevices]);

  // Load device options on mount.
  useEffect(() => {
    loadDeviceOptions();
  }, [loadDeviceOptions]);

  // Auto-save functionality for devices
  const debouncedDevices = useDebounce(assignedDevices, 500);
  useAutoSaveBuildingDevices(
    assignedDevices,
    debouncedDevices,
    updateDeviceData,
    isEditMode
  );

  const handleDeviceFieldChange = useCallback(
    (deviceId, key, value) => {
      const device = assignedDevices.find(
        (device) => device.deviceId === deviceId
      );
      if (device) {
        const updatedDevice = { ...device, [key]: value };
        updateDeviceData(updatedDevice);
      }
    },
    [assignedDevices, updateDeviceData]
  );

  const displayedDevices = useMemo(() => {
    if (!assignedDevices || assignedDevices.length === 0) return [];
    return showAllDevices ? assignedDevices : assignedDevices.slice(0, 10);
  }, [assignedDevices, showAllDevices]);

  const handleRowClick = useCallback(
    (device) => {
      if (
        selectedBuildingDevice &&
        selectedBuildingDevice.deviceId === device.deviceId
      ) {
        dispatch(clearSelectedBuildingDevices());
      } else {
        dispatch(setSelectedBuildingDevices(device));
      }
    },
    [selectedBuildingDevice, dispatch]
  );

  const editHandlers = {
    handleDeviceFieldChange,
    removeDevice: removeDeviceFromBuilding,
  };

  const handleAddNew = () => {
    if (onAddNew) {
      onAddNew();
    }
  };

  return (
    <div className="mb-4">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-10 bg-white">
            <DeviceTableHeader
              isEditMode={isEditMode}
              onAddNew={handleAddNew}
            />
          </thead>
          <tbody>
            {isEditMode && (
              <DeviceRow
                mode="add"
                deviceOptions={deviceOptions}
                addDevice={addDeviceToBuilding}
                newDeviceId={newDeviceId}
                setNewDeviceId={setNewDeviceId}
              />
            )}
            {isEditMode
              ? displayedDevices.map((device, index) => (
                  <DeviceRow
                    key={device.deviceId}
                    mode="edit"
                    device={device}
                    index={index}
                    selectedDevice={selectedBuildingDevice}
                    onRowClick={handleRowClick}
                    handlers={editHandlers}
                  />
                ))
              : displayedDevices.map((device) => (
                  <DeviceRow
                    key={device.deviceId}
                    mode="view"
                    device={device}
                    selectedDevice={selectedBuildingDevice}
                    onRowClick={handleRowClick}
                  />
                ))}
            {assignedDevices.length > 10 && !showAllDevices && (
              <tr>
                <td
                  colSpan={
                    isEditMode ? EDIT_COLUMNS.length + 1 : VIEW_COLUMNS.length
                  }
                  className="text-center"
                >
                  <button
                    onClick={() => setShowAllDevices(true)}
                    className="text-blue-500 hover:underline"
                  >
                    Show all devices
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BuildingDevicesTable;
